import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/shared/ui/data-table';
import {
  RECEIVE_LOCATION_TYPE_LABEL_KEYS,
  RECEIVE_MOVEMENT_LABEL_KEYS,
  RECEIVE_MOVEMENT_TYPE,
  type ReceiveLocationType,
  type ReceiveMovementType,
  type ReceiveTableRow
} from '@/entities/receive';

import {
  getReceiveColumns,
  getReceiveRowActions,
  getReceiveRowHasChildren,
  renderReceiveExpandIcon,
  type ReceiveColumnHandlers
} from '../model/columns';
import { useReceiveLazyRows } from '../model/use-receive-lazy-rows';

interface ReceiveCategoryCardProps {
  movementType: ReceiveMovementType;
  locationType: ReceiveLocationType;
  /** Level-0 rows only — everything deeper is fetched when its row expands. */
  locations: ReceiveTableRow[];
  groupByDate: boolean;
  projectId?: number;
  siteId?: number;
  filterKey: string;
  /** Bumped after a receipt so loaded branches reload — see `useReceiveLazyRows`. */
  dataVersion: number;
  columnHandlers: ReceiveColumnHandlers;
  canExport: boolean;
  canReceive: boolean;
  onExport: () => void;
  isExporting: boolean;
}

// Opaque: an expanded location row sticks over its consignments while they scroll (muted/40 over the card).
const getReceiveRowClassName = (row: ReceiveTableRow): string =>
  row.level === 0 ? 'bg-[color-mix(in_oklab,var(--color-muted)_40%,var(--color-card))] font-medium' : '';

export function ReceiveCategoryCard({
  movementType,
  locationType,
  locations,
  groupByDate,
  projectId,
  siteId,
  filterKey,
  dataVersion,
  columnHandlers,
  canExport,
  canReceive,
  onExport,
  isExporting
}: ReceiveCategoryCardProps) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { rows, expandedRows, handleRowExpand } = useReceiveLazyRows({
    locations,
    projectId,
    siteId,
    groupByDate,
    filterKey,
    dataVersion
  });

  const columns = useMemo(() => getReceiveColumns(t), [t]);
  const actions = useMemo(
    () =>
      getReceiveRowActions(t, columnHandlers, {
        canReceive,
        canReceiveWaybill: movementType === RECEIVE_MOVEMENT_TYPE.INTERNAL_TRANSFERS
      }),
    [t, columnHandlers, canReceive, movementType]
  );

  const label = t(RECEIVE_LOCATION_TYPE_LABEL_KEYS[locationType]);
  const tableId = `receive-${movementType}-${locationType}`.toLowerCase();

  // Customer Deliveries is a single card that shares its accordion's label, so
  // the heading would just repeat it. Comparing the KEYS (not the rendered text)
  // keeps this self-maintaining: give that type its own label and the heading
  // comes back on its own. The fullscreen dialog still uses `label` as its
  // accessible name — opened standalone, it is not redundant there.
  const isLabelledBySection =
    RECEIVE_LOCATION_TYPE_LABEL_KEYS[locationType] === RECEIVE_MOVEMENT_LABEL_KEYS[movementType];

  return (
    <DataTable
      data={rows}
      columns={columns}
      tableId={tableId}
      title={label}
      hideToolbarTitle={isLabelledBySection}
      hierarchical
      expandedRows={expandedRows}
      onRowExpand={handleRowExpand}
      getRowHasChildren={getReceiveRowHasChildren}
      getRowLevel={(row) => row.level}
      renderExpandIcon={renderReceiveExpandIcon}
      rowClassName={getReceiveRowClassName}
      actions={actions}
      selectable
      selectedIds={selectedIds}
      onSelectionChange={setSelectedIds}
      enableColumnCustomization
      enableFullscreen
      enableExport={canExport}
      onExport={onExport}
      isExporting={isExporting}
      height="max-h-[480px]"
    />
  );
}
